import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getChats, markMessagesSeen } from '../../reducers/help-center/helpCenterSlice';
import { Avatar, Box, Grid, Typography } from '@mui/material';
import { JOIN_HELP_CENTER, LEAVES_HELP_ROOM_ID, SEND_MESSAGE_TO_HELP_CENTER } from '../../utils/socket-events';
import socket from '../../utils/socket';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/auth/authSlice';
import moment from 'moment';
import HelpCenterChatBoxFooter from './HelpCenterChatBoxFooter';
import HelpCenterMessage from './HelpCenterMessage';
import Loader from '../common/Loader/Loader';
import { sendNotification } from '../Notification/Notification';
import { NOTIFICATION_IMAGE } from '../../utils/notification-images';
import { HELP_CENTER } from '../../utils/path';

export default function HelpCenterAdminChatBox() {

  const [chats, setChats]  = useState([]);
  const [selectedChat, setSelectedChat] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const dispatch = useDispatch();
  const currentLoginUser = useSelector(selectUser);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchChats = async() => {
      try{
        setIsLoading(true);

        const response = await dispatch(getChats()); 

        socket.emit(JOIN_HELP_CENTER, LEAVES_HELP_ROOM_ID);

        if(response && response.length){
          setChats(response);
        }
      }catch(err){
        console.error(err);
      }
      setIsLoading(false);
    };

    fetchChats();
  },[dispatch]);

  useEffect(() => {
    socket.on(SEND_MESSAGE_TO_HELP_CENTER, ({ message, user, _id }) => {
      const index = chats.findIndex(chat => chat.user._id === user._id);
    
      if(index !== -1){
        setChats(prevChats => {
          const updatedChats = [...prevChats];
          
          const updatedChat = {
            ...updatedChats[index],
            messages: [...updatedChats[index].messages, message]
          };
          
          updatedChats[index] = updatedChat;
          
          return updatedChats;
        });
      }
     
      if(index === -1){
        setChats(prev => [...prev, { message, user, _id }]);
      }

      if(selectedChat.user?._id === user._id){
        setSelectedChat(prev => {
          const updatedChat = {...prev};
          updatedChat.messages = [...updatedChat.messages, message];
          return updatedChat;
        });

        scrollToBottom();

        if(selectedChat.messages.length === 0){
          dispatch(sendNotification(
            dispatch,
            '',
            [currentLoginUser._id],
            [],
            NOTIFICATION_IMAGE,
            'You have new messages from LeavesSupoort',
            HELP_CENTER
          ));
        }
      }
    });

    return () => {
      socket.off(SEND_MESSAGE_TO_HELP_CENTER);
    };
  },[socket, chats]);

  const getLastMessage = (chatId) => {
    const chat = chats.find(chat => chat._id === chatId);
    
    if(chat && chat.messages && chat.messages.length > 0) return chat.messages[chat.messages.length - 1];
    
    return {};
  };

  const getUnreadMessages = (chatId) => {
    const chat = chats.find(chat => chat._id === chatId);
    if(!chat.messages) return [];
    return chat.messages.filter(message => message.sender !== currentLoginUser._id && !message.seen);
  };

  const markMessagesAsSeen = async(chat) => {
    try{
      const index = chats.findIndex(({ _id }) => _id === chat._id);

      if(index !== -1){
        setChats(prevChats => {
          const updatedChats = [...prevChats];
          const updatedMessages = (updatedChats[index]?.messages ||[]).map(msg => ({
            ...msg,
            seen: true  
          }));
        
          updatedChats[index].messages = updatedMessages;
        
          return updatedChats;
        });
      }
     
      await dispatch(markMessagesSeen(chat.user._id,
        getUnreadMessages(chat._id).map(message => message._id))
      );
    }catch(err){
      console.error(err);
    }
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    markMessagesAsSeen(chat);
    scrollToBottom();
  };

  const scrollToBottom = () => {
    if(!containerRef.current) return;
    setTimeout(() => {
      containerRef.current.style.scrollBehavior = 'smooth';
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    },300);
  };

  if(isLoading){
    return <Loader />;
  }

  return (
    <Grid container spacing={2} p={2} sx={{ height: 'calc(100vh - 30px)' }}>
      
      <Grid item xs={3.5} bgcolor={'white'} height={'100%'} overflow={'auto'}>
        {
          chats.map(chat => <Box
            key={chat._id}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            minWidth={300}
            maxWidth={300}
            mb={2} pb={1}
            borderBottom={'2px solid #f5f7fd'}
            className='cursor-pointer'
            onClick={() => handleSelectChat(chat)}
          >
            <Box display={'flex'} alignItems={'center'} columnGap={1.2}>

              <Avatar
                src={chat.user.photoURL} 
                alt={chat.user.name} 
              />

              <Box>
                <Typography variant='body2'>
                  {chat.user.name}
                </Typography>
          
                <Typography variant='caption'>
                  {getLastMessage(chat._id).content}
                </Typography>
              </Box>

            </Box>

            <Box display={'flex'} flexDirection={'column'} rowGap={0.2}>
          
              <Typography variant='caption' display={'block'}>
                {moment(getLastMessage(chat._id).createdAt).format('h:mm A')}
              </Typography>

              <Box 
                width={15} 
                height={15}
                fontSize={9} 
                bgcolor={'#005353'}
                color={'white'} 
                display={'flex'} 
                justifyContent={'center'}
                alignItems={'center'} 
                borderRadius={'50%'}
                visibility={getUnreadMessages(chat._id).length > 0 ? 'visible' : 'hidden'}
              >
                {getUnreadMessages(chat._id).length}
              </Box>

            </Box>

          </Box>
          )
        }
      </Grid>
      
      <Grid item xs={8.5}>
    
        {
          !selectedChat._id && <Typography variant='h6'>
            Select chat to start messaging.
          </Typography>
        }

        <Box sx={{ height: 'calc(100vh - 180px)' }} overflow={'auto'} ref={containerRef}>
          {
            (selectedChat.messages || []).map((message,i) =>  <HelpCenterMessage 
              key={message._id}
              message={message}
              chat={selectedChat}
              index={i}
            />)
          }
        </Box>

        <Box>
          {
            selectedChat.user &&
          <HelpCenterChatBoxFooter
            user={selectedChat.user} 
            sender={currentLoginUser._id} 
          />
          }
        </Box>
      </Grid>
    </Grid>
  );
}
